import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"

const AuthCtx = createContext(null)

const REFRESH_KEY = "auth_refresh"
const REFRESH_INTERVAL_MS = 12 * 60 * 1000

async function apiFetch(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers ?? {}) },
  })
  return res
}

function _applyPrefsToLocalStorage(prefs) {
  if (!prefs || typeof prefs !== "object") return
  if (prefs.startup_page) localStorage.setItem("startup_page", prefs.startup_page)
  if (prefs.ssh) {
    try {
      // Merge over the local cache rather than replacing it. `secondaries` is
      // host-derived (the SSH host is the source of truth; SecondaryAccounts
      // re-fetches it on load) and is NOT reliably part of the server pref blob.
      // A wholesale overwrite here would wipe the cached list on every token
      // refresh/login, making the terminal's profile switcher disappear and
      // hiding secondary sessions from reattach until the next host re-fetch.
      const existing = JSON.parse(localStorage.getItem("ssh_settings") ?? "{}")
      const merged = { ...existing, ...prefs.ssh }
      const serverSecondaries = prefs.ssh.secondaries
      if ((!Array.isArray(serverSecondaries) || serverSecondaries.length === 0) &&
          Array.isArray(existing.secondaries) && existing.secondaries.length > 0) {
        merged.secondaries = existing.secondaries
      }
      localStorage.setItem("ssh_settings", JSON.stringify(merged))
    } catch { /* ignore */ }
    window.dispatchEvent(new Event("ssh-settings-changed"))
  }
  if (prefs.vault) {
    try { localStorage.setItem("vault_settings", JSON.stringify(prefs.vault)) } catch { /* ignore */ }
    window.dispatchEvent(new Event("vault-settings-changed"))
  }
  if (prefs.appearance) {
    try { localStorage.setItem("appearance_settings", JSON.stringify(prefs.appearance)) } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("appearance-settings-changed", { detail: prefs.appearance }))
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const accessTokenRef = useRef(null)
  const refreshTimerRef = useRef(null)

  const setAccess = useCallback((token) => {
    accessTokenRef.current = token
  }, [])

  const scheduleRefresh = useCallback(() => {
    clearInterval(refreshTimerRef.current)
    refreshTimerRef.current = setInterval(async () => {
      const refresh = localStorage.getItem(REFRESH_KEY)
      if (!refresh) return
      try {
        const res = await apiFetch("/api/auth/token/refresh/", {
          method: "POST",
          body: JSON.stringify({ refresh }),
        })
        // 5xx = server temporarily unavailable — keep trying next interval
        if (res.status >= 500) return
        const data = await res.json()
        if (data.access) {
          setAccess(data.access)
          if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh)
        } else {
          // 4xx with no access = token genuinely invalid/expired
          clearInterval(refreshTimerRef.current)
          setAccess(null)
          setUser(null)
          localStorage.removeItem(REFRESH_KEY)
        }
      } catch {
        // network error — keep trying
      }
    }, REFRESH_INTERVAL_MS)
  }, [setAccess])

  const _syncPrefsFromServer = useCallback(async (accessToken) => {
    try {
      const res = await fetch("/api/auth/prefs/", {
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      })
      if (!res.ok) return
      const data = await res.json()
      _applyPrefsToLocalStorage(data.prefs)
    } catch { /* ignore — prefs are optional */ }
  }, [])

  // Restore session from stored refresh token on mount
  useEffect(() => {
    const restore = async () => {
      const refresh = localStorage.getItem(REFRESH_KEY)
      if (!refresh) {
        setLoading(false)
        return
      }
      try {
        // 8-second timeout: if the backend is slow/overloaded the fetch would otherwise
        // hang for minutes before the browser gives up, leaving the user on a blank screen.
        // On timeout an AbortError is thrown → caught below → token kept → login page shown.
        const res = await apiFetch("/api/auth/token/refresh/", {
          method: "POST",
          body: JSON.stringify({ refresh }),
          signal: AbortSignal.timeout(8000),
        })
        // 5xx = server temporarily unavailable (deploy/restart). Don't clear the token —
        // the user lands on /login for this page load but auto-restores on next reload
        // once the server is back up.
        if (res.status >= 500) return
        const data = await res.json()
        if (!data.access) {
          // 4xx with no access token = token genuinely invalid/expired — clear it
          localStorage.removeItem(REFRESH_KEY)
          return
        }
        if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh)
        setAccess(data.access)
        const meRes = await fetch("/api/auth/me/", {
          headers: { Authorization: `Bearer ${data.access}` },
        })
        const meData = await meRes.json()
        if (meData.user) {
          await _syncPrefsFromServer(data.access)
          setUser(meData.user)
          scheduleRefresh()
        }
      } catch {
        // Network error (server down, no connection) — keep the refresh token so the
        // next page load after the server recovers auto-restores the session.
      } finally {
        setLoading(false)
      }
    }
    restore()
    return () => clearInterval(refreshTimerRef.current)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (identifier, password, totpCode = "") => {
    const res = await apiFetch("/api/auth/login/", {
      method: "POST",
      body: JSON.stringify({ username: identifier, password, totp_code: totpCode }),
    })
    const data = await res.json()
    if (data.requires_2fa) return { requires_2fa: true }
    if (!res.ok) return { error: data.error || "Login failed" }
    setAccess(data.access)
    localStorage.setItem(REFRESH_KEY, data.refresh)
    localStorage.setItem("startup_page", "dashboard")
    await _syncPrefsFromServer(data.access)
    setUser(data.user)
    scheduleRefresh()
    return { ok: true }
  }, [setAccess, scheduleRefresh, _syncPrefsFromServer])

  const register = useCallback(async (email, username, password) => {
    const res = await apiFetch("/api/auth/register/", {
      method: "POST",
      body: JSON.stringify({ email, username, password }),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || "Registration failed" }
    setAccess(data.access)
    localStorage.setItem(REFRESH_KEY, data.refresh)
    localStorage.setItem("startup_page", "dashboard")
    setUser(data.user)
    scheduleRefresh()
    return { ok: true }
  }, [setAccess, scheduleRefresh])

  const logout = useCallback(() => {
    clearInterval(refreshTimerRef.current)
    setAccess(null)
    setUser(null)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.setItem("startup_page", "dashboard")
  }, [setAccess])

  const authFetch = useCallback(async (url, options = {}) => {
    const token = accessTokenRef.current
    const res = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers ?? {}),
      },
    })
    if (res.status === 401) {
      const refresh = localStorage.getItem(REFRESH_KEY)
      if (refresh) {
        try {
          const rr = await apiFetch("/api/auth/token/refresh/", {
            method: "POST",
            body: JSON.stringify({ refresh }),
          })
          // 5xx = server temporarily unavailable — keep the token, return the original
          // 401 so the caller sees an error but the user is not logged out.
          if (rr.status >= 500) return res
          const rd = await rr.json()
          if (rd.access) {
            setAccess(rd.access)
            if (rd.refresh) localStorage.setItem(REFRESH_KEY, rd.refresh)
            return fetch(url, {
              ...options,
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${rd.access}`,
                ...(options.headers ?? {}),
              },
            })
          }
        } catch {
          // Network error during refresh — don't logout, return original response
          return res
        }
      }
      // 4xx with no new access token = refresh token genuinely invalid/expired
      logout()
    }
    return res
  }, [logout, setAccess])

  const savePrefs = useCallback(async (partial) => {
    try {
      const res = await authFetch("/api/auth/prefs/", {
        method: "POST",
        body: JSON.stringify(partial),
      })
      if (res.ok) _applyPrefsToLocalStorage(partial)
    } catch { /* ignore */ }
  }, [authFetch])

  const updateProfile = useCallback(async (data) => {
    const res = await authFetch("/api/auth/profile/", {
      method: "PUT",
      body: JSON.stringify(data),
    })
    const json = await res.json()
    if (json.user) setUser(json.user)
    return json
  }, [authFetch])

  const updateAvatar = useCallback(async (avatarB64) => {
    const res = await authFetch("/api/auth/profile/avatar/", {
      method: "POST",
      body: JSON.stringify({ avatar_b64: avatarB64 }),
    })
    if (res.ok) {
      setUser((prev) => prev ? { ...prev, avatar_b64: avatarB64 } : prev)
    }
    return res.json()
  }, [authFetch])

  const refreshUser = useCallback(async () => {
    try {
      const res = await authFetch("/api/auth/me/")
      const data = await res.json()
      if (data.user) setUser(data.user)
    } catch { /* network error */ }
  }, [authFetch])

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, authFetch, savePrefs, updateProfile, updateAvatar, refreshUser }}>
      {children}
    </AuthCtx.Provider>
  )
}

export function useAuth() {
  return useContext(AuthCtx)
}
